import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components/macro';
import { usePrevious } from '../helpers/hooks/usePrevious';
import { assertNotNull } from '../helpers/nullHelpers';
import { colourNeutral10 } from '../styling/colours';
import { spacing04, spacing05, spacing06 } from '../styling/spacing';
import { ButtonRow } from './Button';
import { ErrorBox } from './ErrorBox';
import { Header2 } from './Headers';

type Props = { isOpen: boolean; isWide?: boolean; children?: React.ReactNode };

export const Modal = ({ isOpen, isWide, children }: Props) => {
  const wasPreviouslyOpen = usePrevious(isOpen);

  useEffect(() => {
    const documentHtmlNode = getDocumentHtmlNode();

    if (!wasPreviouslyOpen && isOpen) {
      documentHtmlNode.classList.add(modalIsOpenClassName);
    } else if (wasPreviouslyOpen && !isOpen) {
      documentHtmlNode.classList.remove(modalIsOpenClassName);
    }

    return () => documentHtmlNode.classList.remove(modalIsOpenClassName);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) {
    return null;
  }

  const modalPortalRoot = document.getElementById(modalPortalRootElementId);
  if (modalPortalRoot == null) {
    throw new Error('Could not find modal portal root');
  }

  return createPortal(
    <ModalBackdrop>
      <ModalContentContainer>
        <ModalContent $wide={isWide} role="dialog" aria-label="Modal.">
          <FocusAnchor tabIndex={-1} />
          {children}
        </ModalContent>
      </ModalContentContainer>
    </ModalBackdrop>,
    modalPortalRoot,
  );
};

export const modalPortalRootElementId = 'modal-portal-root';
export const modalIsOpenClassName = 'modal-is-open';

export const getDocumentHtmlNode = () => assertNotNull(document.getElementsByTagName('html').item(0));

export const ModalHeader = styled(Header2)`
  margin-bottom: ${spacing05};
`;

export const ModalActionButtons = styled(ButtonRow)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: ${spacing05} -${spacing05} -${spacing05} -${spacing05};
  padding: ${spacing04} ${spacing05};
  background-color: ${colourNeutral10};
`;

export const ModalErrorBox = styled(ErrorBox)`
  margin-top: ${spacing05};
`;

const backdropFadeIn = keyframes`
  from {
    opacity: 0;
  }
  
  to {
    opacity: 1;
  }
`;

export const ModalBackdrop = styled.div`
  overflow-y: auto;
  position: fixed;
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  animation: ${backdropFadeIn} 250ms ease forwards;
`;

const ModalContentContainer = styled.div`
  margin: auto;
`;

const ModalContent = styled.div<{ $wide?: boolean }>`
  background-color: white;
  padding: ${spacing05};
  min-width: ${props => (props.$wide ? '90vw' : '1100px')};
  max-width: 90vw;
  position: relative;
  margin: ${spacing06} 0;
`;

const FocusAnchor = styled.div`
  height: 0;
  width: 0;
`;
